import laundryAuditClient from "./laundryAuditClient";
import { getLaundryAuditSession, invalidateLaundryAuditSession } from "./laundryAuditAuth";
import { withLookupCache, clearLookups } from "utils/lookupCache";

// Thin wrapper around the Laundry Audit backend, mirroring the shape of
// src/utils/apiCall.js (getApi/postApi -> { error, result }). Every call first
// resolves the Laundry-Audit-specific session (see laundryAuditAuth.js) rather
// than reusing MyM's own token-id/client-id headers directly.

const laHeaders = async () => {
  const { tokenId, userId, clientId } = await getLaundryAuditSession();
  return {
    "token-id": tokenId,
    "user-id": userId,
    "client-id": clientId,
    "device-type": process.env.REACT_APP_LA_DEVICE_TYPE,
  };
};

// Laundry Audit's own token-issuing path rotates the session token on each new
// login for the same user, same as Cleaning Audit's — retry once, with a
// freshly re-minted session, before giving up on a 401.
const withRetryOn401 = async (makeRequest) => {
  try {
    const headers = await laHeaders();
    return await makeRequest(headers);
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    invalidateLaundryAuditSession();
    const headers = await laHeaders();
    return await makeRequest(headers);
  }
};

const fetchGet = async (url) => {
  try {
    const response = await withRetryOn401((headers) => laundryAuditClient.get(url, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// Dropdown/master lists, cached briefly (see utils/lookupCache).
const LOOKUPS = /\/(get_all_statelist_by_country_id|get_all_fac_list_by_state_id)\b/;
export const getLaApi = (url) => withLookupCache("laundry-audit", LOOKUPS, url, () => fetchGet(url));

export const postLaApi = async (url, data) => {
  clearLookups("laundry-audit");
  try {
    const response = await withRetryOn401((headers) => laundryAuditClient.post(url, data, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// ---- Dashboard ----
// Field-for-field identical query shape to Cleaning Audit's dashboard (same
// audit_complited/rectification_complited/rectification_notify columns,
// confirmed by reading MajesticERP-LaundryAuditAPI/code/models/AuditsModel.js directly).
export const getAllAudit = () => getLaApi("/get_all_audit");
export const getAuditsCount = () => getLaApi("/get_audits_count");
export const getAuditsGraphData = () => getLaApi("/get_audits_graph_data");

// ---- Shared: state / facility lists ----
// country_id is hardcoded to 1 (Australia) — matches the old mask's own
// GeneralController.js, which does the same thing.
export const getAllStates = () => getLaApi("/get_all_statelist_by_country_id/1");
export const getFacilitiesByState = (stateId) => getLaApi(`/get_all_fac_list_by_state_id/${stateId}`);

// ---- Questions (no Where/Why sub-bank here, unlike Cleaning Audit — this
// app's question model is flatter: state-scoped question_text/question_info only) ----
export const getQuestionsByState = (stateId) => getLaApi(`/get_audit_question_by_state/${stateId}`);
export const addQuestion = (body) => postLaApi("/create_question", body);
export const updateQuestion = (body) => postLaApi("/update_question", body);
export const deleteQuestion = (id) => getLaApi(`/delete_question/${id}`); // yes, GET — that's how the real API registers it

// ---- Reports ----
export const getAuditFacilityFilter = (body) => postLaApi("/get_audit_facility_filter", body);
export const getPresignedReportUrl = (pdfUrl) => postLaApi("/get_laundry_presigned_url", { url: pdfUrl });

// ---- Audit Register / Audit Tracking ----
// Both already return fully-shaped { facilities: [{ facility_id, facility_name,
// monthly_data: [...12 months] }] } — no client-side grouping needed, unlike
// Cleaning Audit's equivalent screens.
export const getAuditRegister = (body) => postLaApi("/get_audit_register", body);
export const getAuditTrackingReport = (body) => postLaApi("/get_audit_tracking_report", body);
